import React, { useEffect, useState, useMemo, useCallback } from 'react';
import axios from 'axios';
import { ENDPOINTS } from '../../../Utils/Endpoints';
import { Models } from '../../../Utils/ModelsSeguridad';
import AlertDialog from '../../../../Lms/Components/Shared/AlertDialog';
import './RolesRecursosPermisosMatrix.css';

const ID_PERMISO_VER = 1; // "Ver" permission

// Componente de celda memoizado para máximo rendimiento
const MatrixCell = React.memo(({ idRol, idRecurso, hasPermission, onToggle, disabled }) => {
    return (
        <td 
            className={`grid-cell-click ${hasPermission ? 'is-granted' : 'is-denied'} ${disabled ? 'is-busy' : ''}`}
            onClick={() => !disabled && onToggle(idRol, idRecurso)}
        >
            <div className="status-box">
                {hasPermission ? '✓' : '×'}
            </div>
        </td>
    );
});

export default function RolesRecursosPermisosMatrix() {
    const [loading, setLoading] = useState(false);
    const [roles, setRoles] = useState([]);
    const [recursos, setRecursos] = useState([]);
    const [asignaciones, setAsignaciones] = useState([]);
    const [queryRecurso, setQueryRecurso] = useState('');
    const [alertConfig, setAlertConfig] = useState({ open: false, message: '', title: 'Información', variant: 'info' });

    const loadData = async () => {
        setLoading(true);
        try {
            const [resRoles, resRecursos, resAsignaciones] = await Promise.all([
                axios.get(ENDPOINTS.roles),
                axios.get(ENDPOINTS.recursos),
                axios.get(ENDPOINTS.rolesRecursosPermisos)
            ]);

            setRoles((resRoles.data || []).map(Models.roles));
            setRecursos((resRecursos.data || []).map(Models.catRecursos));
            setAsignaciones((resAsignaciones.data || []).map(Models.rolesRecursosPermisos));
        } catch (error) {
            console.error('Error al cargar datos de la matriz:', error);
            setAlertConfig({
                open: true,
                message: 'No se pudieron cargar los datos de permisos.',
                title: 'Error de carga',
                variant: 'error'
            });
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadData();
    }, []);

    // Create a fast lookup map for assignments: Map<"idRol-idRecurso", idAsignacion>
    const assignmentsMap = useMemo(() => {
        const map = new Map();
        asignaciones.forEach(a => {
            if (a.idPermiso === ID_PERMISO_VER) {
                map.set(`${a.idRol}-${a.idRecurso}`, a.idRolRecursoPermiso);
            }
        });
        return map;
    }, [asignaciones]);

    const filteredRecursos = useMemo(() => {
        const q = queryRecurso.toLowerCase().trim();
        if (!q) return recursos;
        return recursos.filter(r => r.nombre.toLowerCase().includes(q));
    }, [recursos, queryRecurso]);

    // Usar useCallback para que la prop onToggle no cambie nunca y no rompa el memo de las celdas
    const handleToggle = useCallback(async (idRol, idRecurso) => {
        // Obtenemos el estado actual de la celda de forma eficiente
        // Nota: Para usar el estado más reciente dentro de un callback memoizado, usamos la forma funcional de setAsignaciones
        setAsignaciones(currentAsignaciones => {
            const key = `${idRol}-${idRecurso}`;
            // Re-calculamos el existingId localmente para el toggle inmediato
            const existing = currentAsignaciones.find(a => a.idRol === idRol && a.idRecurso === idRecurso && a.idPermiso === ID_PERMISO_VER);
            const existingId = existing?.idRolRecursoPermiso;

            const nextAsignaciones = [...currentAsignaciones];
            
            // --- Optimistic Update ---
            if (existingId) {
                // Remove from state
                const filtered = nextAsignaciones.filter(a => a.idRolRecursoPermiso !== existingId);
                
                // Ejecutamos la llamada al API en background SIN esperar al setAsignaciones
                axios.delete(ENDPOINTS.rrpById(existingId)).catch(err => {
                    console.error('Error al borrar:', err);
                    setAsignaciones(currentAsignaciones); // Revert
                    setAlertConfig({ open: true, message: 'Fallo al sincronizar el borrado.', title: 'Error', variant: 'error' });
                });

                return filtered;
            } else {
                // Add temp
                const tempId = -Math.round(Math.random() * 1000000);
                const newAsignacion = {
                    idRolRecursoPermiso: tempId,
                    idRol,
                    idRecurso,
                    idPermiso: ID_PERMISO_VER,
                    fechaAsignacion: new Date().toISOString()
                };
                
                // API Call
                const payload = { idRol, idRecurso, idPermiso: ID_PERMISO_VER };
                axios.post(ENDPOINTS.rolesRecursosPermisos, payload).then(response => {
                    const createdId = typeof response.data === 'number' ? response.data : (response.data.idRolRecursoPermiso || 0);
                    const realItem = { ...newAsignacion, idRolRecursoPermiso: createdId };
                    
                    setAsignaciones(prev => prev.map(a => 
                        (a.idRol === idRol && a.idRecurso === idRecurso && a.idRolRecursoPermiso < 0) 
                        ? realItem : a
                    ));
                }).catch(err => {
                    console.error('Error al insertar:', err);
                    setAsignaciones(currentAsignaciones); // Revert
                    setAlertConfig({ open: true, message: 'Fallo al sincronizar el guardado.', title: 'Error', variant: 'error' });
                });

                return [...nextAsignaciones, newAsignacion];
            }
        });
    }, []);

    return (
        <div className="panel-sigma-main matrix-panel-adjust">
                <div className="roles-topbar">
                    <h3 className="roles-title">Matriz de Permisos (Ver)</h3>
                    <input 
                        className="roles-search"
                        placeholder="Filtrar por recurso..." 
                        value={queryRecurso}
                        onChange={(e) => setQueryRecurso(e.target.value)}
                    />
                </div>

                <div className="roles-table-wrap matrix-table-card-override">
                    <div className="roles-table-scroller matrix-scroller-override">
                        <table className="roles-table matrix-table-override">
                            <thead>
                                <tr>
                                    <th className="sticky-col first-col header-corner">
                                        <div className="header-corner-content">Recurso \ Rol</div>
                                    </th>
                                    {roles.map(rol => (
                                        <th key={rol.idRol} className="rol-header-cell">
                                            <div className="rol-name-vertical" title={rol.nombre}>{rol.nombre}</div>
                                        </th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {filteredRecursos.map(recurso => (
                                    <tr key={recurso.idRecurso}>
                                        <td className="sticky-col first-col recurso-name-cell">
                                            <div className="recurso-label">{recurso.nombre}</div>
                                        </td>
                                        {roles.map(rol => (
                                            <MatrixCell 
                                                key={`${rol.idRol}-${recurso.idRecurso}`}
                                                idRol={rol.idRol}
                                                idRecurso={recurso.idRecurso}
                                                hasPermission={assignmentsMap.has(`${rol.idRol}-${recurso.idRecurso}`)}
                                                onToggle={handleToggle}
                                                disabled={loading}
                                            />
                                        ))}
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>

            <AlertDialog
                open={alertConfig.open}
                title={alertConfig.title}
                message={alertConfig.message}
                variant={alertConfig.variant}
                onClose={() => setAlertConfig({ ...alertConfig, open: false })}
            />
        </div>
    );
}
